/**
 * admin-wallet — formatting helpers (F-012 Phase 3, IMPL-P3-002).
 *
 * The admin API serializes BigInt monetary fields (`fidoLimitCents`,
 * `balanceCents`, …) as decimal *strings* and `Date` fields as ISO strings (see
 * `serializeBigInt` in `apps/api/src/routes/admin/clienti.ts`). These helpers
 * therefore accept `string` cents and ISO date strings.
 */

/**
 * Format a cents amount (as a decimal string, BigInt or number) into a localized
 * euro string, e.g. `"€ 1.234,56"`. Handles negative values (overdraft).
 */
export function centsToEuro(cents: string | bigint | number | null | undefined): string {
  if (cents === null || cents === undefined || cents === '') return '—';
  let value: number;
  if (typeof cents === 'bigint') {
    value = Number(cents) / 100;
  } else if (typeof cents === 'number') {
    value = cents / 100;
  } else {
    const parsed = Number(cents);
    if (Number.isNaN(parsed)) return '—';
    value = parsed / 100;
  }
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

/** Convert a cents string/BigInt to a plain euro `number` (for client-side math). */
export function centsToEuroNumber(cents: string | bigint | number | null | undefined): number {
  if (cents === null || cents === undefined || cents === '') return 0;
  if (typeof cents === 'bigint') return Number(cents) / 100;
  if (typeof cents === 'number') return cents / 100;
  const parsed = Number(cents);
  return Number.isNaN(parsed) ? 0 : parsed / 100;
}

/** Convert a cents string/BigInt to a plain cents `number` (for client-side math). */
export function centsToNumber(cents: string | bigint | number | null | undefined): number {
  if (cents === null || cents === undefined || cents === '') return 0;
  if (typeof cents === 'bigint') return Number(cents);
  if (typeof cents === 'number') return cents;
  const parsed = Number(cents);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Format an ISO date string as `dd/mm/yyyy` (Italian locale). Returns `'—'`
 * when input is empty. Mirrors `features/archivio/lib/format.ts`.
 */
export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('it-IT');
  } catch {
    return iso.slice(0, 10);
  }
}

/** Format an ISO date-time string as `dd/mm/yyyy HH:mm` (Italian locale). */
export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso.slice(0, 16);
  }
}

/* -------------------------------------------------------------------------- */
/* Domain labels                                                              */
/* -------------------------------------------------------------------------- */

/** Italian label for the `tipologia` business types shown in the admin list. */
export const TIPOLOGIA_LABEL: Record<string, string> = {
  AGENTE: 'Agente',
  COMMERCIALISTA: 'Commercialista',
  PRIVATO: 'Privato',
};

export function tipologiaLabel(tipologia: string | null | undefined): string {
  if (!tipologia) return '—';
  return TIPOLOGIA_LABEL[tipologia] ?? tipologia;
}

/** Wallet state derived from `walletBlocked` + `gracePeriodUntil`. */
export type WalletState = 'blocked' | 'grace' | 'active';

export function walletState(account: {
  walletBlocked: boolean;
  gracePeriodUntil: string | null;
}): WalletState {
  if (account.walletBlocked) return 'blocked';
  if (account.gracePeriodUntil) return 'grace';
  return 'active';
}

export const WALLET_STATE_LABEL: Record<WalletState, string> = {
  blocked: 'Bloccato',
  grace: 'Grace period',
  active: 'Attivo',
};

/** Italian label for a `FidoAuditEntry.action`. */
export const ACTION_LABEL: Record<string, string> = {
  SET: 'Modifica',
  GRACE: 'Grace period',
  BLOCK: 'Blocco',
  UNBLOCK: 'Sblocco',
};

export function actionLabel(action: string): string {
  return ACTION_LABEL[action] ?? action;
}
